import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, ShoppingBag, Clock, Table2,
  RefreshCw, ArrowUpRight, CheckCircle2, Loader2,
} from 'lucide-react';
import { getRealtimeDashboard, type RealtimeDashboard } from '../../services/DashboardService';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatCOP(v: number) {
  return '$ ' + Math.round(v).toLocaleString('es-CO');
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
}

const PAYMENT_COLORS: Record<string, string> = {
  PAID:    'text-emerald-600',
  PENDING: 'text-amber-600',
  PARTIAL: 'text-blue-600',
};
const PAYMENT_LABELS: Record<string, string> = {
  PAID: 'Pagado', PENDING: 'Pendiente', PARTIAL: 'Parcial',
};
const FULFILLMENT_LABELS: Record<string, string> = {
  DINE_IN: 'Mesa', MESA: 'Mesa', TAKEOUT: 'Para llevar',
  TAKEAWAY: 'Para llevar', DELIVERY: 'Domicilio',
};

// ── KPI Card ───────────────────────────────────────────────────────────────────

interface KpiProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  accent?: string;
  loading?: boolean;
}

function KpiCard({ label, value, sub, icon, accent = 'bg-primary/10 text-primary', loading }: KpiProps) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-5 shadow-card">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
        {loading ? (
          <div className="mt-1 h-7 w-28 animate-pulse rounded bg-muted" />
        ) : (
          <p className="mt-0.5 text-2xl font-bold text-text">{value}</p>
        )}
        {sub && !loading && (
          <p className="mt-0.5 text-xs text-text-muted">{sub}</p>
        )}
      </div>
    </div>
  );
}

// ── Chart tooltip ──────────────────────────────────────────────────────────────

function HourTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3 text-sm shadow-card">
      <p className="mb-1 font-semibold text-text">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.fill }}>
          {p.name}: {p.dataKey === 'revenue' ? formatCOP(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

// ── Auto-refresh countdown ─────────────────────────────────────────────────────

const REFRESH_SECS = 30;

function RefreshCountdown({ onRefresh, isFetching }: { onRefresh: () => void; isFetching: boolean }) {
  const [remaining, setRemaining] = useState(REFRESH_SECS);
  const ref = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    setRemaining(REFRESH_SECS);
    ref.current = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) { onRefresh(); return REFRESH_SECS; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, []);

  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-xs font-medium text-text-muted">
      {isFetching
        ? <Loader2 size={14} className="animate-spin text-primary" />
        : <RefreshCw size={14} />}
      Refresca en {remaining}s
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery<RealtimeDashboard>({
    queryKey: ['dashboard', 'realtime'],
    queryFn: getRealtimeDashboard,
    refetchInterval: REFRESH_SECS * 1000,
    staleTime: 15_000,
  });

  const today = data?.today;
  const recentOrders = data?.recentOrders ?? [];
  const topProducts = data?.topProducts ?? [];

  // Only render hours from 6:00 onward (or earlier if there are orders)
  const hourly = (data?.hourly ?? []).filter(h => h.hour >= 6 || h.orders > 0);

  const hasHourlyData = hourly.some(h => h.orders > 0);

  const updatedAt = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div className="min-h-screen bg-bg p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Dashboard</h1>
            <p className="text-sm text-text-muted">
              {updatedAt ? `Actualizado a las ${updatedAt}` : 'Cargando datos…'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <RefreshCountdown onRefresh={() => refetch()} isFetching={isFetching} />
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow transition hover:opacity-90 disabled:opacity-60"
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              Refrescar
            </button>
          </div>
        </div>

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard
            label="Ingresos hoy"
            value={formatCOP(today?.totalRevenue ?? 0)}
            sub={`${today?.totalOrders ?? 0} pedido${today?.totalOrders === 1 ? '' : 's'} cobrado${today?.totalOrders === 1 ? '' : 's'}`}
            icon={<TrendingUp size={20} />}
            accent="bg-emerald-100 text-emerald-700"
            loading={isLoading}
          />
          <KpiCard
            label="Ticket promedio"
            value={formatCOP(today?.avgTicket ?? 0)}
            icon={<ShoppingBag size={20} />}
            accent="bg-blue-100 text-blue-700"
            loading={isLoading}
          />
          <KpiCard
            label="Pedidos activos"
            value={String(today?.activeOrders ?? 0)}
            sub="En espera o preparando"
            icon={<Clock size={20} />}
            accent="bg-amber-100 text-amber-700"
            loading={isLoading}
          />
          <KpiCard
            label="Mesas ocupadas"
            value={`${today?.occupiedTables ?? 0} / ${today?.totalTables ?? 0}`}
            sub={today ? `${today.totalTables - today.occupiedTables} libre${today.totalTables - today.occupiedTables === 1 ? '' : 's'}` : ''}
            icon={<Table2 size={20} />}
            accent="bg-purple-100 text-purple-700"
            loading={isLoading}
          />
        </div>

        {/* ── Charts + Recent orders ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">

          {/* Hourly bar chart */}
          <div className="lg:col-span-3 rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Ventas por hora — hoy</h2>
            {isLoading ? (
              <div className="flex h-52 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : !hasHourlyData ? (
              <div className="flex h-52 items-center justify-center text-sm text-text-muted">
                Sin ventas registradas hoy todavía
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={hourly} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border,#e5e7eb)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={24} />
                  <YAxis
                    yAxisId="right" orientation="right"
                    tick={{ fontSize: 10 }} width={52}
                    tickFormatter={v => '$ ' + (v / 1000).toFixed(0) + 'k'}
                  />
                  <Tooltip content={<HourTooltip />} />
                  <Bar yAxisId="left"  dataKey="orders"  name="Pedidos"  fill="#6366f1" radius={[4,4,0,0]} />
                  <Bar yAxisId="right" dataKey="revenue" name="Ingresos" fill="#22c55e" radius={[4,4,0,0]} opacity={0.75} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Recent orders */}
          <div className="lg:col-span-2 rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Últimos pedidos</h2>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-9 w-9 animate-pulse rounded-xl bg-muted" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-28 animate-pulse rounded bg-muted" />
                      <div className="h-3 w-16 animate-pulse rounded bg-muted" />
                    </div>
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : recentOrders.length === 0 ? (
              <p className="text-sm text-text-muted">Sin pedidos recientes</p>
            ) : (
              <ul className="space-y-3">
                {recentOrders.map(order => {
                  const label = order.customerName?.trim() || order.tableName || 'Sin nombre';
                  const type  = FULFILLMENT_LABELS[order.fulfillmentType ?? ''] ?? 'Mesa';
                  const payColor = PAYMENT_COLORS[order.paymentStatus] ?? 'text-text-muted';
                  const payLabel = PAYMENT_LABELS[order.paymentStatus] ?? order.paymentStatus;
                  return (
                    <li key={order.id} className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-bold text-primary">
                        #{order.id}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-text">{label}</p>
                        <p className="text-xs text-text-muted">{type} · {timeAgo(order.createdAt)}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-bold text-text">{formatCOP(order.total)}</p>
                        <p className={`text-xs font-semibold ${payColor}`}>{payLabel}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ── Top products today ───────────────────────────────────────────── */}
        {(isLoading || topProducts.length > 0) && (
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-text">Top productos hoy</h2>
            {isLoading ? (
              <div className="flex flex-wrap gap-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-9 w-36 animate-pulse rounded-xl bg-muted" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {topProducts.map((p, idx) => (
                  <div
                    key={p.name}
                    className={`flex items-center gap-2 rounded-xl border px-4 py-2 ${
                      idx === 0
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-border bg-bg'
                    }`}
                  >
                    {idx === 0 && <CheckCircle2 size={14} className="text-primary shrink-0" />}
                    <span className={`text-sm font-medium ${idx === 0 ? 'text-text' : 'text-text-muted'}`}>
                      {p.name}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-text-muted">
                      {p.qty} uds.
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
